// Fleet → Reconcile.
//
// NEW IN V2, and it took two wrong turns to get right — both worth recording
// so the next person does not repeat them.
//
// The two device registries are independent BY DESIGN. edge_nodes_devices is
// the commercial asset (procurement → tenant deployment) and exists whether
// or not GaliGali OS is ever installed. ggos_devices is the technical /
// operational view of a device in the field and exists whether or not we
// procured the hardware. They meet on hardware identifiers. Non-overlap is
// therefore normal, not breakage.
//
// The second trap: measured across ALL inventory, 40 of 66 rows carry no
// hardware identifier and coverage looks like a damning 39%. Split by
// platform it is the opposite story —
//
//     in_cab       24 rows   24 bridged   100%
//     transit_tv   23 rows    1 bridged
//     billboard    19 rows    1 bridged
//
// — because billboards and transit TV are DOOH placements that never run our
// Android OS. The edge-nodes PRD's "100% hardware ID tracking" target is MET
// exactly where it applies. So this screen reports coverage PER PLATFORM and
// treats a platform that does not run GGOS as out of scope rather than as a
// failure. A single global percentage here would invent a crisis.

import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHead, Tiles, DataTable, Pill, Loading, ErrorState, Button, ui } from '../../components/ui';
import useApi from '../../lib/useApi';
import fetchAllPages from '../../lib/fetchAllPages';
import api from '../../../services/api';

const hasId = (r) => Boolean(r.imei || r.serialNumber || r.widevineId);

// A platform counts as GGOS-capable when most of its rows actually bridge.
// Derived from the data rather than hard-coded, so a new platform type
// classifies itself instead of silently reading as broken.
const GGOS_THRESHOLD = 0.5;

function summarise(rows) {
  const by = new Map();
  for (const r of rows) {
    const k = r.platformType || 'unknown';
    if (!by.has(k)) by.set(k, { platform: k, total: 0, withId: 0, bridged: 0 });
    const g = by.get(k);
    g.total += 1;
    if (hasId(r)) g.withId += 1;
    if (r.iotThingName) g.bridged += 1;
  }
  for (const g of by.values()) {
    g.rate = g.total ? g.bridged / g.total : 0;
    g.ggos = g.rate >= GGOS_THRESHOLD;
  }
  return [...by.values()].sort((a, b) => b.rate - a.rate || b.total - a.total);
}

const LABEL = { unbridgeable: 'No hardware ID', 'not-in-field': 'Not in field', bridged: 'Bridged', 'out-of-scope': 'Not a GGOS platform' };
const TONE  = { unbridgeable: 'bad', 'not-in-field': 'warn', bridged: 'grow', 'out-of-scope': 'pend' };

export default function Reconcile() {
  const [scope, setScope] = useState('ggos');
  const { data, error, loading, reload } = useApi(
    () => fetchAllPages(({ page, pageSize }) => api.get('/devices', { params: { page, pageSize } })), []);

  const all = useMemo(() => data?.rows || [], [data]);
  const groups = useMemo(() => summarise(all), [all]);
  const ggosPlatforms = useMemo(
    () => new Set(groups.filter((g) => g.ggos).map((g) => g.platform)), [groups]);

  const rows = useMemo(() => all.map((r) => {
    const inScope = ggosPlatforms.has(r.platformType);
    const state = !inScope ? 'out-of-scope'
      : !hasId(r) ? 'unbridgeable'
      : !r.iotThingName ? 'not-in-field'
      : 'bridged';
    return { ...r, state, inScope };
  }), [all, ggosPlatforms]);

  if (loading) return <><Head /><Loading what="inventory" /></>;
  if (error)   return <><Head /><ErrorState error={error} onRetry={reload} /></>;

  const shown = scope === 'ggos' ? rows.filter((r) => r.inScope) : rows;
  const inScope = rows.filter((r) => r.inScope);
  const n = (st) => inScope.filter((r) => r.state === st).length;
  const pct = inScope.length ? Math.round((n('bridged') / inScope.length) * 100) : 0;

  return (
    <>
      <Head onRefresh={reload} />

      <Tiles items={[
        { label: 'GGOS inventory', value: inScope.length, foot: [...ggosPlatforms].join(', ') || 'none' },
        { label: 'Bridged',        value: n('bridged'), tone: 'grow' },
        { label: 'No hardware ID', value: n('unbridgeable'), tone: n('unbridgeable') ? 'bad' : undefined,
          foot: 'cannot ever match' },
        { label: 'Not in field',   value: n('not-in-field'), tone: n('not-in-field') ? 'warn' : undefined,
          foot: 'identified, no OS seen' },
        { label: 'Coverage',       value: `${pct}%`, tone: pct >= 100 ? 'grow' : 'warn', foot: 'PRD target: 100%' },
        { label: 'Out of scope',   value: rows.length - inScope.length, foot: 'DOOH placements' },
      ]} />

      <div className={ui.stubCard} style={{ marginBottom: 'var(--s5)' }}>
        <div style={{ fontFamily: 'var(--f-mono)', fontSize: 10.5, letterSpacing: '.08em',
                      textTransform: 'uppercase', color: 'var(--ink-mute)', marginBottom: 10 }}>
          Coverage by platform
        </div>
        <table className={ui.table} style={{ width: 'auto', minWidth: 460 }}>
          <thead><tr>
            <th>Platform</th><th className={ui.num}>Rows</th>
            <th className={ui.num}>With ID</th><th className={ui.num}>Bridged</th><th>Scope</th>
          </tr></thead>
          <tbody>
            {groups.map((g) => (
              <tr key={g.platform}>
                <td>{g.platform}</td>
                <td className={ui.num}>{g.total}</td>
                <td className={ui.num}>{g.withId}</td>
                <td className={ui.num}>{g.bridged}</td>
                <td>{g.ggos
                  ? <Pill tone={g.bridged === g.total ? 'grow' : 'warn'}>
                      {Math.round(g.rate * 100)}% GGOS
                    </Pill>
                  : <Pill tone="pend">not GGOS</Pill>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <DataTable
        rows={shown}
        getKey={(r) => r.id}
        filterOn={['nodeName', 'tenantName', 'city', 'imei', 'serialNumber', 'iotThingName', 'platformType']}
        filterPlaceholder="Filter by name, tenant, city, IMEI…"
        initialSort={{ key: 'state', dir: 'asc' }}
        right={
          <Button onClick={() => setScope((s) => (s === 'ggos' ? 'all' : 'ggos'))}>
            {scope === 'ggos' ? 'Show all inventory' : 'GGOS platforms only'}
          </Button>
        }
        columns={[
          { key: 'state', label: 'State', width: 150,
            render: (r) => <Pill tone={TONE[r.state]}>{LABEL[r.state]}</Pill>,
            sort: (r) => ({ unbridgeable: 0, 'not-in-field': 1, bridged: 2, 'out-of-scope': 3 }[r.state]) },
          { key: 'nodeName', label: 'Inventory name',
            render: (r) => <Link to={`/devices/${r.id}`} style={{ color: 'var(--iris-text)' }}>{r.nodeName || '—'}</Link> },
          { key: 'platformType', label: 'Platform' },
          { key: 'tenantName', label: 'Tenant', render: (r) => r.tenantName || <Pill tone="pend">unassigned</Pill> },
          { key: 'city', label: 'City' },
          { key: 'imei', label: 'IMEI', mono: true },
          { key: 'serialNumber', label: 'Serial', mono: true },
          { key: 'iotThingName', label: 'Field device', mono: true,
            render: (r) => (r.iotThingName
              ? <Link to={`/ggos-devices/${r.iotThingName}`} style={{ color: 'var(--iris-text)' }}>{r.iotThingName}</Link>
              : null) },
        ]}
      />

      {data && !data.complete && (
        <p style={{ marginTop: 'var(--s4)', fontSize: 13, color: 'var(--warn-text)' }}>
          Showing the first {all.length.toLocaleString()} of {data.total.toLocaleString()} inventory rows —
          the counts above cover only what was loaded.
        </p>
      )}

      <p style={{ marginTop: 'var(--s4)', fontSize: 13, color: 'var(--ink-mute)', maxWidth: '80ch' }}>
        Coverage is judged only on platforms that actually run GaliGali OS — a platform qualifies when
        most of its rows bridge, so a new type classifies itself. Billboards and transit TV are DOOH
        placements and are counted out of scope, not as failures. “Not in field” is a normal resting
        state: inventory can sit in a warehouse for months. “No hardware ID” on a GGOS platform is the
        only genuine defect here, because such a row can never be matched however many devices install.
      </p>
    </>
  );
}

const Head = ({ onRefresh }) => (
  <PageHead
    title="Reconcile inventory to the field"
    sub="Inventory and field devices are separate registries on purpose — each exists without the other. They meet on hardware identifiers, and this is how completely, per platform."
    actions={onRefresh ? <Button onClick={onRefresh}>Refresh</Button> : null}
  />
);
